import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { createUserAvatarUploadUrl } from "@/loaders/presigned-upload";

import { ClearIcon, UploadIcon } from "./icons";

interface UploadButtonProps {
  children?: React.ReactNode;
}

function UploadButton({ children }: UploadButtonProps) {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-2 rounded-full border border-white/20 border-dashed transition-colors group-hover:bg-white/10">
      <UploadIcon />
      {children || (
        <p className="text-center text-13/[1.54em] text-white">
          Upload
          <br />
          photo
        </p>
      )}
    </div>
  );
}

interface ImageUploadProps {
  children?: React.ReactNode;
  className?: string;
  image?: string;
  onChange(imageKey: string): void;
  onProgress(inProgress: boolean): void;
  withClearButton?: boolean;
}

export default function ImageUpload({
  children,
  className,
  image,
  onChange,
  onProgress,
  withClearButton = false,
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(image || null);

  const uploadImage = async (imageFile: File) => {
    try {
      const { key, url } = await createUserAvatarUploadUrl({
        data: { ext: imageFile.name.split(".").pop() || "" },
      });
      onChange(key);
      await fetch(url, {
        body: imageFile,
        method: "PUT",
      });
    } catch {
      onChange("");
    } finally {
      onProgress(false);
    }
  };

  const handleClear = () => {
    setPreview("");
    onChange("");
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget;

    if (!files?.length) {
      return;
    }

    setPreview(URL.createObjectURL(files[0]));
    onProgress(true);
    uploadImage(files[0]);
  };

  const previewSrc = useMemo(
    () => (preview === null ? image : preview),
    [image, preview]
  );

  return (
    <div
      className={cn("group relative flex size-30 justify-center", className)}
    >
      {previewSrc ? (
        <>
          <img
            alt="avatar"
            className="size-full rounded-full object-cover object-center"
            src={previewSrc}
          />
          {withClearButton && (
            <Button
              aria-label="Clear"
              className="absolute top-0 right-0 z-1 size-10 rounded-full border border-white/20 bg-black/50 md:size-8"
              onClick={handleClear}
            >
              <ClearIcon className="size-4.5 md:size-[15px]" />
            </Button>
          )}
        </>
      ) : (
        <UploadButton>{children}</UploadButton>
      )}
      <Input
        accept="image/*"
        className="absolute inset-0 h-auto cursor-pointer opacity-0"
        data-testid="image-upload-input"
        onChange={handleFile}
        type="file"
      />
    </div>
  );
}
